// A sheet from the bottom, as iOS draws one: glass, a grabber, a large radius, swipe down or
// tap outside to dismiss, Escape on a keyboard. The tab bar stays where it is beneath it.
import type { ComponentChildren } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";

export function Sheet({ open, onClose, label, children, tall }: { open: boolean; onClose: () => void; label: string; children: ComponentChildren; tall?: boolean }) {
  const [dy, setDy] = useState(0);
  const start = useRef<number | null>(null);
  useEffect(() => {
    if (!open) return;
    const k = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", k);
    document.body.dataset.sheet = "1";
    // A tall sheet holds a field at its foot, so it follows the visible viewport: when the
    // keyboard opens, the sheet shortens above it rather than hiding the field underneath.
    const vv = tall ? window.visualViewport : null;
    const fit = () => { if (!vv) return; const r = document.documentElement.style; r.setProperty("--vvh", `${vv.height}px`); r.setProperty("--vvt", `${vv.offsetTop}px`); };
    fit(); vv?.addEventListener("resize", fit); vv?.addEventListener("scroll", fit);
    return () => { window.removeEventListener("keydown", k); delete document.body.dataset.sheet; vv?.removeEventListener("resize", fit); vv?.removeEventListener("scroll", fit); };
  }, [open]);
  if (!open) return null;
  const down = (e: PointerEvent) => { start.current = e.clientY; (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); };
  const move = (e: PointerEvent) => { if (start.current != null) setDy(Math.max(0, e.clientY - start.current)); };
  const up = () => { if (dy > 90) onClose(); setDy(0); start.current = null; };
  return (
    <>
      <div class="sheet-scrim" onClick={onClose} />
      <section class={`sheet${tall ? " tall" : ""}`} role="dialog" aria-modal="true" aria-label={label} style={dy ? { transform: `translateY(${dy}px)`, transition: "none" } : undefined}>
        <div class="sheet-grab" onPointerDown={down as any} onPointerMove={move as any} onPointerUp={up} onPointerCancel={up}>
          <span aria-hidden="true" />
        </div>
        <div class="sheet-body">{children}</div>
      </section>
    </>
  );
}

/** A ring in segments (rides) or as a fill (km). The done part is amber, the rest quiet. */
export function Ring({ done, of, segments = true, size = 88, children }: { done: number; of: number; segments?: boolean; size?: number; children?: ComponentChildren }) {
  const r = size / 2 - 5, c = 2 * Math.PI * r;
  const n = Math.max(1, of);
  const gap = segments && n > 1 ? 6 : 0;
  const seg = c / n - gap;
  return (
    <div class="ring" style={{ width: `${size}px`, height: `${size}px` }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
          {segments ? Array.from({ length: n }, (_, i) => (
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke-width="6" stroke-linecap="round"
              class={i < done ? "ring-on" : "ring-off"} stroke-dasharray={`${seg} ${c - seg}`} stroke-dashoffset={-(i * (seg + gap)) - gap / 2} />
          )) : (
            <>
              <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke-width="6" class="ring-off" />
              <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke-width="6" stroke-linecap="round" class="ring-on" stroke-dasharray={`${Math.min(1, done / n) * c} ${c}`} />
            </>
          )}
        </g>
      </svg>
      <div class="ring-in">{children}</div>
    </div>
  );
}

/** A small glass note above the dock, gone after four seconds, with an optional Undo. */
export function useToast() {
  const [t, setT] = useState<{ text: string; undo?: () => void } | null>(null);
  const timer = useRef<number>();
  const show = (text: string, undo?: () => void) => { setT({ text, undo }); clearTimeout(timer.current); timer.current = window.setTimeout(() => setT(null), 4000); };
  const node = t ? (
    <div class="toast" role="status">
      <span>{t.text}</span>
      {t.undo && <button type="button" onClick={() => { t.undo!(); setT(null); }}>Undo</button>}
    </div>
  ) : null;
  return { show, node };
}
